import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/contracts`;

interface RenewalItem { contract_id: number; contract_number: string; title: string; contract_type: string; counterparty_id: string; total_value: number; expiration_date: string; days_until_expiry: number; auto_renewal: boolean; has_renewal: boolean; urgency: string; recommendation: string; }

const URGENCY_COLORS: Record<string, string> = { LOW: 'var(--positive)', HIGH: 'var(--warning)', CRITICAL: 'var(--negative)' };

export default function ContractRenewals() {
  const { entity } = useGlobalEntity();
  const [items, setItems] = useState<RenewalItem[]>([]);
  const [autoAlerts, setAutoAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('queue');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, aRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/renewals/queue?days=365`, entity)),
        authFetch(withEntityParam(`${API}/renewals/auto-alerts`, entity)),
      ]);
      if (qRes.ok) { const d = await qRes.json(); setItems(d.items || []); }
      if (aRes.ok) { const d = await aRes.json(); setAutoAlerts(d.alerts || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleRenew = async (id: number) => {
    const res = await authFetch(`${API}/${id}/renew`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: 'Renewal' }) });
    if (res.ok) { showToast('Renewal created', true); load(); } else { const e = await res.json(); showToast(e.detail || 'Failed', false); }
  };

  const tabs = [
    { key: 'queue', label: 'Renewal Queue', count: items.length },
    { key: 'auto', label: 'Auto-Renewal Alerts', count: autoAlerts.length },
  ];

  const queueColumns: Column[] = [
    { key: 'contract_number', label: 'Contract', render: (v: string, row: RenewalItem) => (
      <div>
        <div style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>{row.title}</div>
      </div>
    )},
    { key: 'contract_type', label: 'Type', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'counterparty_id', label: 'Counterparty', render: (v: string) => v || '-' },
    { key: 'total_value', label: 'Value', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'expiration_date', label: 'Expiry' },
    { key: 'days_until_expiry', label: 'Days', render: (v: number, row: RenewalItem) => <span style={{ fontWeight: 700, color: URGENCY_COLORS[row.urgency] }}>{v}d</span> },
    { key: 'urgency', label: 'Urgency', render: (v: string, row: RenewalItem) => (
      <span style={{ fontWeight: 700, color: URGENCY_COLORS[v], fontSize: 11 }}>
        {v}{row.auto_renewal && <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--warning)' }}>AUTO</span>}
      </span>
    )},
    { key: 'actions', label: 'Actions', render: (_: any, row: RenewalItem) => !row.has_renewal
      ? <ActionButton label="Renew" onClick={() => handleRenew(row.contract_id)} variant="primary" size="sm" />
      : <span style={{ fontSize: 11, color: 'var(--positive)' }}>Initiated</span>
    },
  ];

  return (
    <PageLayout title="Contract Renewals" subtitle="Upcoming renewals, auto-renewal alerts, and action queue">
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : tab === 'queue' ? (
        <DataTable columns={queueColumns} rows={items} rowKey="contract_id" emptyMessage="No contracts expiring within 365 days." />
      ) : (
        autoAlerts.length === 0 ? (
          <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No auto-renewal alerts.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {autoAlerts.map((a, i) => (
              <div key={i} style={{ padding: 12, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--warning)', fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: 'var(--warning)' }}>{a.contract_number} — {a.days_until_expiry} days</div>
                <div style={{ color: 'var(--text-secondary)', marginTop: 4 }}>{a.message}</div>
              </div>
            ))}
          </div>
        )
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
